import React from 'react';
import { Phone, MessageSquare, Mail, Chrome, Eye, EyeOff, Fingerprint, Facebook, Lock, User, ArrowRight } from 'lucide-react';

const PhoneLoginModal = ({
  mobile,
  setMobile,
  password,
  checkPasswordStrength,
  passwordStrength,
  showPassword,
  setShowPassword,
  sendOTPForLogin,
  sendOTP,
  loginMethod,
  setLoginMethod,
  handleEmailLogin,
  rememberMe,
  setRememberMe,
  handleSocialLogin,
  biometricEnabled,
  handleBiometricLogin,
  socialMediaLoginEnabled,
  showForgotPassword,
  showSignUp,
  error,
  setError
}) => {
  return (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-md rounded-3xl p-8 shadow-2xl my-8 transform transition-all hover:scale-[1.01] duration-300 relative z-10">
        {/* Header with App Logo */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-300">
              <span className="text-5xl transform -rotate-3">🥛</span>
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white text-xs font-bold">✓</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">Welcome Back!</h1>
          <p className="text-gray-600">Sign in to access fresh dairy delivered daily</p>
        </div>

        {/* Biometric Login Option */}
        {biometricEnabled && (
          <div className="mb-6">
            <button
              onClick={handleBiometricLogin}
              className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 hover:shadow-2xl hover:shadow-purple-500/50 transition-all transform hover:scale-[1.02] active:scale-[0.98] group"
            >
              <Fingerprint size={24} className="group-hover:rotate-12 transition-transform" />
              <span>Sign in with Biometrics</span>
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-gray-300"></div>
              <span className="text-gray-500 text-sm font-medium px-2">OR CONTINUE WITH</span>
              <div className="flex-1 h-0.5 bg-gradient-to-l from-transparent via-gray-300 to-gray-300"></div>
            </div>
          </div>
        )}

        {loginMethod === 'phone' && (
          <>
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                <Phone size={16} className="text-blue-600" />
                Mobile Number
              </label>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity pointer-events-none"></div>
                <div className="relative flex items-center border-2 border-gray-200 rounded-2xl px-4 py-4 focus-within:border-blue-500 focus-within:shadow-lg focus-within:shadow-blue-500/20 transition-all bg-white hover:bg-gray-50">
                  <input
                    type="tel"
                    maxLength="10"
                    value={mobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setMobile(value);
                      if (error && setError) setError(''); // Clear error when user starts typing
                    }}
                    placeholder="Enter your mobile number"
                    autoFocus
                    className="flex-1 outline-none text-gray-800 text-lg font-medium bg-transparent placeholder:text-gray-400"
                  />
                  {mobile.length === 10 && (
                    <div className="ml-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 text-sm font-medium">{error}</p>
                </div>
              </div>
            )}
            {/* Password Input and OTP Buttons 
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => checkPasswordStrength(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 pr-10 sm:pr-12 outline-none focus:border-blue-500 transition-all text-sm sm:text-base"
                  id="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
                </button>
              </div>
              
              {password && passwordStrength.score > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-all ${
                          level <= passwordStrength.score
                            ? passwordStrength.color
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-600">
                    Password strength: <span className="font-semibold">{passwordStrength.label}</span>
                  </p>
                </div>
              )}
              
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  showForgotPassword();
                }}
                className="text-xs sm:text-sm text-blue-600 hover:underline mt-2"
              >
                Forgot Password?
              </button>
            </div>
            */}

            <div className="space-y-3 mb-6">
              <button
                onClick={() => sendOTPForLogin('sms')}
                disabled={mobile.length !== 10}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 hover:shadow-2xl hover:shadow-blue-500/50 transition-all disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:shadow-none transform hover:scale-[1.02] active:scale-[0.98] group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                <MessageSquare size={20} className="relative z-10" />
                <span className="relative z-10">Send OTP via SMS</span>
                <ArrowRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => sendOTP('whatsapp')}
                disabled={mobile.length !== 10}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 hover:shadow-2xl hover:shadow-green-500/50 transition-all disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:shadow-none transform hover:scale-[1.02] active:scale-[0.98] group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                <MessageSquare size={20} className="relative z-10" />
                <span className="relative z-10">Send OTP via WhatsApp</span>
                <ArrowRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </>
        )}

        {loginMethod === 'email' && (
          <>
            <div className="mb-3 sm:mb-4">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                placeholder="your.email@example.com"
                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 outline-none focus:border-blue-500 transition-all text-sm sm:text-base"
                id="email-input"
              />
            </div>

            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => checkPasswordStrength(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 pr-10 sm:pr-12 outline-none focus:border-blue-500 transition-all text-sm sm:text-base"
                  id="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
                </button>
              </div>
              
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  showForgotPassword();
                }}
                className="text-xs sm:text-sm text-blue-600 hover:underline mt-2"
              >
                Forgot Password?
              </button>
            </div>

            <button
              onClick={() => {
                const email = document.getElementById('email-input').value;
                const pwd = document.getElementById('password-input').value;
                handleEmailLogin(email, pwd);
              }}
              className="w-full bg-blue-600 text-white py-3 sm:py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all mb-3 sm:mb-4 text-sm sm:text-base"
            >
              Sign In with Email
            </button>

            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              <input
                type="checkbox"
                id="remember-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="remember-me" className="text-xs sm:text-sm text-gray-700">
                Remember me on this device
              </label>
            </div>
          </>
        )}

        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-gray-300"></div>
          <span className="text-gray-500 text-sm font-medium">OR</span>
          <div className="flex-1 h-0.5 bg-gradient-to-l from-transparent via-gray-300 to-gray-300"></div>
        </div>
        {socialMediaLoginEnabled && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => handleSocialLogin('Google')}
            className="bg-white border-2 border-gray-200 text-gray-700 py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-gray-50 hover:border-blue-500 hover:text-blue-600 transition-all transform hover:scale-105 active:scale-95 group"
          >
            <Chrome size={20} className="text-blue-600 group-hover:rotate-12 transition-transform" />
            <span>Google</span>
          </button>

          <button
            onClick={() => handleSocialLogin('Facebook')}
            className="bg-white border-2 border-gray-200 text-gray-700 py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-gray-50 hover:border-blue-600 hover:text-blue-600 transition-all transform hover:scale-105 active:scale-95 group"
          >
            <Facebook size={20} className="text-blue-600 group-hover:rotate-12 transition-transform" fill="currentColor" />
            <span>Facebook</span>
          </button>
        </div>
        )}
        <div className="flex gap-2 justify-center mb-4">
          <button
            onClick={() => setLoginMethod('phone')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              loginMethod === 'phone'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Phone size={16} className="inline mr-1" />
            Phone
          </button>
          <button
            onClick={() => setLoginMethod('email')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              loginMethod === 'email'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Mail size={16} className="inline mr-1" />
            Email
          </button>
        </div>

        <p className="text-xs text-gray-400 text-center mb-4">
          By continuing, you agree to our{' '}
          <button className="text-blue-600 hover:underline">Terms</button>
          {' '}and{' '}
          <button className="text-blue-600 hover:underline">Privacy Policy</button>
        </p>

        <div className="text-center pt-4 border-t border-gray-200">
          <p className="text-gray-600 text-sm">
            Don't have an account?{' '}
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                showSignUp();
              }}
              className="text-blue-600 font-bold hover:text-purple-600 transition-colors inline-flex items-center gap-1 group"
            >
              <span>Create Account</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </p>
          <p className="text-xs text-gray-400 mt-4">Protected by enterprise-grade security 🔒</p>
        </div>
      </div>
    </div>
  );
};

export default PhoneLoginModal;